import { Globe, Loader2, Search, X } from "lucide-react"
import { useEffect, useRef, useState } from "react"

import { detectDomain } from "@/components/onboarding/types"
import type { BusinessSuggestion } from "@/components/onboarding/types"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

// Extracted from components/onboarding/step-search.tsx so the admin's
// account form can reuse the same place-picker UX in a compact field
// layout. Onboarding remains the canonical "what should this look like"
// reference; if the design changes there, change here too.
//
// IMP-390: type-ahead via Places Autocomplete. Rows show name + locality
// only; picking one resolves the website/address through the details
// endpoint. A session token groups the keystrokes and the details fetch
// into one billed Places session.

export interface PlacesPickerSelection {
  place_id: string
  name: string
  website: string | null
  address: string | null
}

export function PlacesPicker({
  value,
  onChange,
  placeholder = "Business name or website URL",
  ariaLabel,
  searchPath = "/onboarding/search",
  detailsPath = "/onboarding/place_details",
}: {
  value: PlacesPickerSelection | null
  onChange: (selection: PlacesPickerSelection | null) => void
  placeholder?: string
  ariaLabel?: string
  // IMP-289: the JSON places-search endpoint to hit. Defaults to the client
  // onboarding routes; the admin + partner portals pass their scoped endpoints.
  searchPath?: string
  // IMP-390: the JSON place-details endpoint that resolves a picked
  // prediction's website + address.
  detailsPath?: string
}) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<BusinessSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [resolving, setResolving] = useState(false)
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // One Places session per lookup: minted on the first keystroke, spent by
  // the details fetch on selection.
  const sessionTokenRef = useRef<string | null>(null)

  useEffect(() => {
    if (value) return
    if (query.trim().length < 2) {
      setResults([])
      setOpen(false)
      setLoading(false)
      return
    }
    setLoading(true)
    setOpen(true)
    sessionTokenRef.current ??= crypto.randomUUID()
    const controller = new AbortController()
    const timer = setTimeout(() => {
      const params = new URLSearchParams({
        query,
        session_token: sessionTokenRef.current ?? "",
      })
      fetch(`${searchPath}?${params.toString()}`, {
        signal: controller.signal,
        headers: { Accept: "application/json" },
      })
        .then((r) => r.json())
        .then((data: { results: BusinessSuggestion[] }) => {
          setResults(data.results ?? [])
          setLoading(false)
        })
        .catch((e: unknown) => {
          if ((e as Error).name === "AbortError") return
          setLoading(false)
        })
    }, 200)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query, value, searchPath])

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handle)
    return () => document.removeEventListener("mousedown", handle)
  }, [])

  function finalize(selection: PlacesPickerSelection) {
    onChange(selection)
    setOpen(false)
    setQuery("")
    setResults([])
    setResolving(false)
  }

  // Predictions only carry name + locality; resolve the website and address
  // before handing the selection up. Failures degrade to the prediction's
  // name — the server-side scrape re-fetches details anyway.
  function choosePrediction(r: BusinessSuggestion) {
    if (resolving) return
    setResolving(true)
    const token = sessionTokenRef.current
    sessionTokenRef.current = null
    const params = new URLSearchParams({ place_id: r.place_id })
    if (token) params.set("session_token", token)
    fetch(`${detailsPath}?${params.toString()}`, {
      headers: { Accept: "application/json" },
    })
      .then((res) => res.json())
      .then(
        (data: {
          result?: {
            name?: string | null
            website?: string | null
            address?: string | null
          }
        }) => {
          finalize({
            place_id: r.place_id,
            name: data.result?.name ?? r.name,
            website: data.result?.website ?? null,
            address: data.result?.address ?? null,
          })
        },
      )
      .catch(() => {
        finalize({
          place_id: r.place_id,
          name: r.name,
          website: null,
          address: null,
        })
      })
  }

  function chooseFallback(selection: PlacesPickerSelection) {
    sessionTokenRef.current = null
    finalize(selection)
  }

  function clear() {
    onChange(null)
    setQuery("")
    setResults([])
    setTimeout(() => inputRef.current?.focus(), 50)
  }

  if (value) {
    return (
      <div className="bg-muted/40 flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
        <div className="min-w-0">
          <p className="truncate font-medium">{value.name}</p>
          {value.website && (
            <p className="text-muted-foreground truncate text-xs">
              {value.website}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={clear}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Clear selection"
        >
          <X className="size-4" />
        </button>
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          ref={inputRef}
          placeholder={placeholder}
          aria-label={ariaLabel}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (results.length > 0) setOpen(true)
          }}
          className="pl-9 text-sm placeholder:text-sm"
        />
        {(loading || resolving) && (
          <Loader2 className="text-muted-foreground absolute top-1/2 right-3 size-4 -translate-y-1/2 animate-spin" />
        )}
      </div>

      {open && (
        <div className="bg-popover absolute z-20 mt-1.5 w-full overflow-hidden rounded-lg border shadow-md">
          {loading && results.length === 0 ? (
            <div className="text-muted-foreground flex items-center gap-2 px-4 py-3 text-sm">
              <Loader2 className="size-3.5 animate-spin" /> Searching…
            </div>
          ) : results.length === 0 ? (
            <FallbackRow query={query} onChoose={chooseFallback} />
          ) : (
            <div>
              {results.map((r, i) => (
                <button
                  key={r.place_id}
                  type="button"
                  disabled={resolving}
                  className={cn(
                    "hover:bg-muted/60 flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors disabled:opacity-60",
                    i > 0 && "border-t",
                  )}
                  onClick={() => choosePrediction(r)}
                >
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-blue-500 text-xs font-semibold text-white">
                    {r.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{r.name}</p>
                    {r.locality && (
                      <p className="text-muted-foreground truncate text-xs">
                        {r.locality}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function FallbackRow({
  query,
  onChoose,
}: {
  query: string
  onChoose: (r: PlacesPickerSelection) => void
}) {
  const domain = detectDomain(query)
  const displayValue = domain ?? query
  const isUrl = domain !== null
  return (
    <button
      type="button"
      onClick={() =>
        onChoose({
          place_id: isUrl ? `url:${domain}` : `raw:${query}`,
          name: displayValue,
          website: isUrl ? domain : null,
          address: null,
        })
      }
      className="hover:bg-muted/60 flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors"
    >
      {isUrl ? (
        <div className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-md">
          <Globe className="text-muted-foreground size-4" />
        </div>
      ) : (
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-blue-500 text-xs font-semibold text-white">
          {displayValue.charAt(0).toUpperCase()}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          Choose {isUrl ? displayValue : `"${displayValue}"`}
        </p>
        <p className="text-muted-foreground truncate text-xs">
          We couldn&apos;t find a matching business — continue with this anyway
        </p>
      </div>
    </button>
  )
}
